import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  AlertCircle, 
  CreditCard, 
  Home, 
  Settings, 
  Gavel, 
  Megaphone,
  Calendar,
  Euro,
  FileText,
  Building2,
  Calculator,
  Users
} from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';

const AddCostModal = ({ isOpen, onClose, onSuccess, properties = [], editingCost = null, initialData = null }) => {
  const [formData, setFormData] = useState({
    property_id: '',
    category: '',
    cost_type: '',
    description: '',
    amount: '',
    vat_rate: '19.00',
    frequency: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    // Category-specific fields
    loan_reference: '',
    bank_name: '',
    management_company: '',
    contract_reference: '',
    percentage_of_rent: '',
    supplier_name: '',
    invoice_number: '',
    due_date: '',
    service_provider: '',
    case_reference: '',
    service_date: '',
    platform_name: '',
    vacancy_start_date: '',
    vacancy_end_date: '',
    expected_monthly_rent: ''
  });

  const [loading, setLoading] = useState(false);

  // Handle initial data from OCR
  useEffect(() => {
    if (initialData && !editingCost) {
      console.log("🔄 Pre-filling form with OCR data:", initialData);
      setFormData(prev => ({
        ...prev,
        property_id: initialData.property_id || prev.property_id,
        category: initialData.category || prev.category,
        description: initialData.description || prev.description,
        amount: initialData.gross_amount || initialData.net_amount || prev.amount,
        vat_rate: initialData.vat_rate || prev.vat_rate,
        start_date: initialData.invoice_date || prev.start_date,
        supplier_name: initialData.supplier_name || prev.supplier_name,
        invoice_number: initialData.invoice_number || prev.invoice_number,
        due_date: initialData.due_date || prev.due_date
      }));
    }
  }, [initialData, editingCost]);
  const [errors, setErrors] = useState({});

  // Calculate amounts automatically
  const netAmount = parseFloat(formData.amount) || 0;
  const vatRate = parseFloat(formData.vat_rate) || 0;
  const vatAmount = (netAmount * vatRate) / 100;
  const grossAmount = netAmount + vatAmount;

  const costCategories = [
    {
      id: 'financing',
      name: 'Finanzierung',
      icon: CreditCard,
      color: 'blue',
      table: 'financing_costs',
      types: ['interest', 'principal', 'loan_fees', 'bank_charges'],
      typeLabels: {
        interest: 'Zinsen',
        principal: 'Tilgung',
        loan_fees: 'Kreditgebühren',
        bank_charges: 'Bankgebühren'
      }
    },
    {
      id: 'management',
      name: 'Verwaltung',
      icon: Home,
      color: 'green',
      table: 'management_fees',
      types: ['property_management', 'administration', 'accounting', 'legal_admin'],
      typeLabels: {
        property_management: 'Hausverwaltung',
        administration: 'Administration',
        accounting: 'Buchhaltung',
        legal_admin: 'Rechtsverwaltung'
      }
    },
    {
      id: 'operating',
      name: 'Betrieb',
      icon: Settings,
      color: 'orange',
      table: 'operating_costs',
      types: ['property_tax', 'insurance', 'utilities', 'cleaning', 'gardening', 'winter_service', 'waste_disposal', 'caretaker'],
      typeLabels: {
        property_tax: 'Grundsteuer',
        insurance: 'Versicherung',
        utilities: 'Nebenkosten',
        cleaning: 'Reinigung',
        gardening: 'Gartenpflege',
        winter_service: 'Winterdienst',
        waste_disposal: 'Müllentsorgung',
        caretaker: 'Hausmeister'
      }
    },
    {
      id: 'legal',
      name: 'Recht & Steuern',
      icon: Gavel,
      color: 'purple',
      table: 'legal_costs',
      types: ['lawyer', 'notary', 'court_fees', 'government_fees', 'broker_commission', 'registration_fees'],
      typeLabels: {
        lawyer: 'Anwalt',
        notary: 'Notar',
        court_fees: 'Gerichtsgebühren',
        government_fees: 'Behördengebühren',
        broker_commission: 'Maklergebühren',
        registration_fees: 'Eintragungsgebühren'
      }
    },
    {
      id: 'marketing',
      name: 'Marketing',
      icon: Megaphone,
      color: 'pink',
      table: 'marketing_costs',
      types: ['advertising', 'listing_fees', 'photography', 'staging', 'vacancy_loss', 'broker_fees', 'online_portals'],
      typeLabels: {
        advertising: 'Werbung',
        listing_fees: 'Inseratsgebühren',
        photography: 'Fotografie',
        staging: 'Home Staging',
        vacancy_loss: 'Leerstandskosten',
        broker_fees: 'Maklergebühren',
        online_portals: 'Online-Portale'
      }
    }
  ];

  // Effect to populate form when editing
  useEffect(() => {
    if (editingCost && isOpen) {
      // Map the cost data to form fields
      const mappedData = {
        property_id: editingCost.property_id || '',
        category: editingCost.category || '',
        cost_type: editingCost.cost_type || editingCost.fee_type || '',
        description: editingCost.description || '',
        amount: editingCost.amount || '',
        vat_rate: editingCost.vat_rate || 19,
        frequency: editingCost.frequency || 'monthly',
        start_date: editingCost.start_date || editingCost.billing_period_start || editingCost.service_date || '',
        end_date: editingCost.end_date || editingCost.billing_period_end || '',
        
        // Category-specific fields
        bank_name: editingCost.bank_name || '',
        loan_reference: editingCost.loan_reference || '',
        management_company: editingCost.management_company || '',
        contract_reference: editingCost.contract_reference || '',
        percentage_of_rent: editingCost.percentage_of_rent || '',
        supplier_name: editingCost.supplier_name || '',
        invoice_number: editingCost.invoice_number || '',
        due_date: editingCost.due_date || '',
        service_provider: editingCost.service_provider || '',
        case_reference: editingCost.case_reference || '',
        service_date: editingCost.service_date || '',
        platform_name: editingCost.platform_name || '',
        vacancy_start_date: editingCost.vacancy_start_date || '',
        vacancy_end_date: editingCost.vacancy_end_date || '',
        expected_monthly_rent: editingCost.expected_monthly_rent || '',
        
        // Recovery fields
        is_recoverable: editingCost.is_recoverable || false,
        recovery_percentage: editingCost.recovery_percentage || 100
      };
      
      setFormData(mappedData);
    } else if (!editingCost && isOpen) {
      // Reset form for new cost
      setFormData({
        property_id: '',
        category: '',
        cost_type: '',
        description: '',
        amount: '',
        vat_rate: 19,
        frequency: 'monthly',
        start_date: '',
        end_date: '',
        bank_name: '',
        loan_reference: '',
        management_company: '',
        contract_reference: '',
        percentage_of_rent: '',
        supplier_name: '',
        invoice_number: '',
        due_date: '',
        service_provider: '',
        case_reference: '',
        service_date: '',
        platform_name: '',
        vacancy_start_date: '',
        vacancy_end_date: '',
        expected_monthly_rent: '',
        is_recoverable: false,
        recovery_percentage: 100
      });
    }
  }, [editingCost, isOpen]);

  const selectedCategory = costCategories.find(cat => cat.id === formData.category);

  const handleInputChange = (field, value) => {
    console.log(`🔍 DEBUG: handleInputChange called - field: ${field}, value:`, value);
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      console.log(`🔍 DEBUG: Updated formData for ${field}:`, newData);
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.property_id) newErrors.property_id = 'Objekt ist erforderlich';
    if (!formData.category) newErrors.category = 'Kategorie ist erforderlich';
    if (!formData.cost_type) newErrors.cost_type = 'Kostenart ist erforderlich';
    if (!formData.description) newErrors.description = 'Beschreibung ist erforderlich';
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Gültiger Betrag ist erforderlich';
    }
    if (!formData.start_date) newErrors.start_date = 'Startdatum ist erforderlich';

    // Category-specific validation
    if (formData.category === 'financing') {
      if (!formData.bank_name) newErrors.bank_name = 'Bankname ist erforderlich';
    }
    
    if (formData.category === 'management') {
      if (!formData.management_company) newErrors.management_company = 'Verwaltungsunternehmen ist erforderlich';
    }

    if (formData.category === 'operating') {
      if (!formData.supplier_name) newErrors.supplier_name = 'Lieferant ist erforderlich';
    }

    if (formData.category === 'legal') {
      if (!formData.service_provider) newErrors.service_provider = 'Dienstleister ist erforderlich';
    }

    if (formData.category === 'marketing') {
      if (formData.cost_type === 'vacancy_loss' && !formData.expected_monthly_rent) {
        newErrors.expected_monthly_rent = 'Erwartete Monatsmiete ist erforderlich';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🔍 DEBUG: Form submission started');
    console.log('🔍 DEBUG: Current formData:', formData);
    
    if (!validateForm()) {
      console.log('🔍 DEBUG: Validation failed, errors:', errors);
      return;
    }

    setLoading(true);
    console.log('🔍 DEBUG: Validation passed, proceeding with submission');

    try {
      const category = costCategories.find(cat => cat.id === formData.category);
      if (!category) {
        throw new Error('Ungültige Kategorie');
      }

      // Get current user
      const { data: { user }, error: userError } = await masterSupabase.auth.getUser();
      if (userError || !user) {
        throw new Error('Benutzer nicht authentifiziert');
      }

      // Prepare data for the specific table
      const baseData = {
        property_id: formData.property_id || null, // ✅ FIX: Convert empty string to null for UUID field
        description: formData.description,
        amount: netAmount, vat_amount: vatAmount, gross_amount: grossAmount, // ✅ FIXED: was net_amount
        vat_rate: parseFloat(formData.vat_rate),
        frequency: formData.frequency,
        is_active: true,
        created_by: user.id,
      };

      // Add category-specific fields
      let specificData = {};
      
      switch (formData.category) {
        case 'financing':
          specificData = {
            cost_type: formData.cost_type,
            start_date: formData.start_date,
            end_date: formData.end_date || null,
            loan_reference: formData.loan_reference,
            bank_name: formData.bank_name
          };
          break;
          
        case 'management':
          specificData = {
            fee_type: formData.cost_type,
            start_date: formData.start_date,
            end_date: formData.end_date || null,
            percentage_of_rent: formData.percentage_of_rent ? parseFloat(formData.percentage_of_rent) : null,
            management_company: formData.management_company,
            contract_reference: formData.contract_reference
          };
          break;
          
        case 'operating':
          specificData = {
            cost_type: formData.cost_type,
            billing_period_start: formData.start_date,
            billing_period_end: formData.end_date || null,
            supplier_name: formData.supplier_name,
            invoice_number: formData.invoice_number,
            due_date: formData.due_date || null,
            is_recoverable: formData.is_recoverable || false,
            recovery_percentage: formData.is_recoverable ? (parseFloat(formData.recovery_percentage) || 100) : 0,
            cost_date: formData.start_date // Add cost_date for filtering
          };
          break;
          
        case 'legal':
          specificData = {
            cost_type: formData.cost_type,
            service_provider: formData.service_provider,
            case_reference: formData.case_reference,
            invoice_number: formData.invoice_number,
            service_date: formData.service_date || formData.start_date,
            due_date: formData.due_date || null
          };
          break;
          
        case 'marketing':
          specificData = {
            cost_type: formData.cost_type,
            platform_name: formData.platform_name,
            vacancy_start_date: formData.vacancy_start_date || null,
            vacancy_end_date: formData.vacancy_end_date || null,
            expected_monthly_rent: formData.expected_monthly_rent ? parseFloat(formData.expected_monthly_rent) : null,
            service_provider: formData.service_provider,
            invoice_number: formData.invoice_number,
            service_date: formData.service_date || formData.start_date,
            due_date: formData.due_date || null
          };
          break;
      }

      const finalData = { ...baseData, ...specificData };

      console.log('🔍 DEBUG: Final data being sent to database:', finalData);
      console.log('🔍 DEBUG: Table:', category.table);
      console.log('🔍 DEBUG: supplier_name in finalData:', finalData.supplier_name);
      console.log('🔍 DEBUG: is_recoverable in finalData:', finalData.is_recoverable);
      console.log('🔍 DEBUG: recovery_percentage in finalData:', finalData.recovery_percentage);

      let data, error;

      if (editingCost) {
        console.log('🔍 DEBUG: Updating existing cost with ID:', editingCost.id);
        // Update existing cost
        const updateResult = await masterSupabase
          .from(category.table)
          .update(finalData)
          .eq('id', editingCost.id)
          .select();
        
        data = updateResult.data;
        error = updateResult.error;
        console.log('🔍 DEBUG: Update result:', { data, error });
      } else {
        console.log('🔍 DEBUG: Inserting new cost');
        // Insert new cost
        const insertResult = await masterSupabase
          .from(category.table)
          .insert([finalData])
          .select();
        
        data = insertResult.data;
        error = insertResult.error;
        console.log('🔍 DEBUG: Insert result:', { data, error });
      }

      if (error) {
        console.error('❌ Supabase error:', error);
        throw error;
      }

      console.log(editingCost ? '✅ Cost updated successfully:' : '✅ Cost added successfully:', data);
      
      // Reset form
      setFormData({
        property_id: '',
        category: '',
        cost_type: '',
        description: '',
        amount: '',
        vat_rate: '19.00',
        frequency: 'monthly',
        start_date: new Date().toISOString().split('T')[0],
        end_date: '',
        loan_reference: '',
        bank_name: '',
        management_company: '',
        contract_reference: '',
        percentage_of_rent: '',
        supplier_name: '',
        invoice_number: '',
        due_date: '',
        service_provider: '',
        case_reference: '',
        service_date: '',
        platform_name: '',
        vacancy_start_date: '',
        vacancy_end_date: '',
        expected_monthly_rent: ''
      });

      onSuccess && onSuccess();
      onClose();

    } catch (error) {
      console.error('❌ Error adding cost:', error);
      setErrors({ submit: error.message || 'Fehler beim Hinzufügen der Kosten' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {editingCost ? 'Kosten bearbeiten' : 'Neue Kosten hinzufügen'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {errors.submit && (
            <div className="flex items-center p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-3" />
              <span className="text-red-700 dark:text-red-300">{errors.submit}</span>
            </div>
          )}

          {/* Property Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Building2 className="w-4 h-4 inline mr-2" />
              Objekt *
            </label>
            <select
              value={formData.property_id}
              onChange={(e) => handleInputChange('property_id', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.property_id ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <option value="">Objekt auswählen</option>
              {properties.map(property => (
                <option key={property.id} value={property.id}>
                  {property.property_name}
                </option>
              ))}
            </select>
            {errors.property_id && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.property_id}</p>
            )}
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kategorie *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {costCategories.map((category) => {
                const Icon = category.icon;
                const isSelected = formData.category === category.id;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      handleInputChange('category', category.id);
                      handleInputChange('cost_type', ''); // Reset cost type when category changes
                    }}
                    className={`p-3 border rounded-lg text-left transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-2" />
                    <div className="text-sm font-medium">{category.name}</div>
                  </button>
                );
              })}
            </div>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.category}</p>
            )}
          </div>

          {/* Cost Type Selection */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Kostenart *
              </label>
              <select
                value={formData.cost_type}
                onChange={(e) => handleInputChange('cost_type', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.cost_type ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">Kostenart auswählen</option>
                {selectedCategory.types.map(type => (
                  <option key={type} value={type}>
                    {selectedCategory.typeLabels[type]}
                  </option>
                ))}
              </select>
              {errors.cost_type && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.cost_type}</p>
              )}
            </div>
          )}

          {/* Basic Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FileText className="w-4 h-4 inline mr-2" />
                Beschreibung *
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="z.B. Monatliche Zinszahlung"
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Euro className="w-4 h-4 inline mr-2" />
                Betrag (Netto) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => handleInputChange('amount', e.target.value)}
                placeholder="0,00"
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.amount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.amount}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                MwSt. (%)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.vat_rate}
                onChange={(e) => handleInputChange('vat_rate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Häufigkeit
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => handleInputChange('frequency', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="monthly">Monatlich</option>
                <option value="quarterly">Vierteljährlich</option>
                <option value="yearly">Jährlich</option>
                <option value="one_time">Einmalig</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Startdatum *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => handleInputChange('start_date', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.start_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.start_date && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.start_date}</p>
              )}
            </div>
          </div>

          {/* LIVE CALCULATION DISPLAY */}
          {formData.amount && parseFloat(formData.amount) > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Calculator className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Kostenberechnung
                </h3>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Netto-Betrag</div>
                  <div className="text-lg font-semibold text-gray-900 dark:text-white">
                    {netAmount.toFixed(2)} €
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                    MwSt ({vatRate.toFixed(1)}%)
                  </div>
                  <div className="text-lg font-semibold text-orange-600 dark:text-orange-400">
                    {vatAmount.toFixed(2)} €
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Gesamtkosten</div>
                  <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                    {grossAmount.toFixed(2)} €
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recovery/Umlagefähigkeit Section - Only for operating costs */}
          {formData.category === 'operating' && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Users className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                <h3 className="text-sm font-medium text-green-900 dark:text-green-100">
                  Umlagefähigkeit auf Mieter
                </h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_recoverable"
                    checked={formData.is_recoverable || false}
                    onChange={(e) => {
                      console.log('Checkbox changed:', e.target.checked);
                      setFormData(prev => ({
                        ...prev,
                        is_recoverable: e.target.checked,
                        recovery_percentage: e.target.checked ? 100 : 0
                      }));
                    }}
                    className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:focus:ring-green-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="is_recoverable" className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                    Diese Kosten sind auf Mieter umlagefähig
                  </label>
                </div>
                
                {formData.is_recoverable && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Umlagefähiger Anteil (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.recovery_percentage || 100}
                        onChange={(e) => {
                          console.log('Recovery percentage changed:', e.target.value);
                          setFormData(prev => ({
                            ...prev,
                            recovery_percentage: parseFloat(e.target.value) || 0
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Geben Sie an, welcher Prozentsatz der Kosten auf die Mieter umgelegt werden kann
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Umlagefähiger Betrag
                      </label>
                      <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg">
                        <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                          {((grossAmount * (parseFloat(formData.recovery_percentage) || 100)) / 100).toFixed(2)} €
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          von {grossAmount.toFixed(2)} € Gesamtkosten
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Category-specific fields */}
          {formData.category === 'financing' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bankname *
                </label>
                <input
                  type="text"
                  value={formData.bank_name}
                  onChange={(e) => handleInputChange('bank_name', e.target.value)}
                  placeholder="z.B. Deutsche Bank"
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.bank_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.bank_name && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.bank_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Kreditreferenz
                </label>
                <input
                  type="text"
                  value={formData.loan_reference}
                  onChange={(e) => handleInputChange('loan_reference', e.target.value)}
                  placeholder="z.B. KR-2024-001"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {formData.category === 'management' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Verwaltungsunternehmen *
                </label>
                <input
                  type="text"
                  value={formData.management_company}
                  onChange={(e) => handleInputChange('management_company', e.target.value)}
                  placeholder="z.B. Hausverwaltung Schmidt"
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.management_company ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.management_company && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.management_company}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prozent der Miete (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={formData.percentage_of_rent}
                  onChange={(e) => handleInputChange('percentage_of_rent', e.target.value)}
                  placeholder="z.B. 3.5"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {formData.category === 'operating' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Lieferant/Anbieter *
                </label>
                <input
                  type="text"
                  value={formData.supplier_name}
                  onChange={(e) => handleInputChange('supplier_name', e.target.value)}
                  placeholder="z.B. Stadtwerke Mannheim"
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.supplier_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.supplier_name && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.supplier_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rechnungsnummer
                </label>
                <input
                  type="text"
                  value={formData.invoice_number}
                  onChange={(e) => handleInputChange('invoice_number', e.target.value)}
                  placeholder="z.B. RE-2024-001"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {formData.category === 'legal' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Dienstleister *
                </label>
                <input
                  type="text"
                  value={formData.service_provider}
                  onChange={(e) => handleInputChange('service_provider', e.target.value)}
                  placeholder="z.B. Rechtsanwaltskanzlei Müller"
                  className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.service_provider ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.service_provider && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.service_provider}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Aktenzeichen
                </label>
                <input
                  type="text"
                  value={formData.case_reference}
                  onChange={(e) => handleInputChange('case_reference', e.target.value)}
                  placeholder="z.B. AZ-2024-001"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {formData.category === 'marketing' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Plattform/Anbieter
                  </label>
                  <input
                    type="text"
                    value={formData.platform_name}
                    onChange={(e) => handleInputChange('platform_name', e.target.value)}
                    placeholder="z.B. ImmobilienScout24"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                {formData.cost_type === 'vacancy_loss' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Erwartete Monatsmiete *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.expected_monthly_rent}
                      onChange={(e) => handleInputChange('expected_monthly_rent', e.target.value)}
                      placeholder="0,00"
                      className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.expected_monthly_rent ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                    {errors.expected_monthly_rent && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.expected_monthly_rent}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {editingCost ? 'Aktualisieren...' : 'Speichern...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingCost ? 'Aktualisieren' : 'Speichern'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCostModal;

